import { Badge, Button, Modal, Spinner, Table } from "react-bootstrap"
import { MdDelete } from "react-icons/md"
import { useState } from "react"
import useFetch from "../../../_fetch"
import { INetworkIntegration } from "./network-settings"

export function NetworkTable({ network, fetchUserData }: { network: INetworkIntegration, fetchUserData: () => Promise<void> }) {
    const { doFetch } = useFetch()
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [credentialsId, setCredentialsId] = useState("")
    const [isRemoving, setIsRemoving] = useState(false)
    const [removeError, setRemoveError] = useState(false)
    const [updatingCredentialsId, setUpdatingCredentialsId] = useState("")
    const [updateError, setUpdateError] = useState(false)

    const handleDelete = (id: string) => {
        setShowDeleteModal(true)
        setRemoveError(false)
        setCredentialsId(id)
    }

    const confirmDelete = async () => {
        setIsRemoving(true)
        setRemoveError(false)
        try {
            const res = await doFetch("/remove-network-credentials", { credentialsId })
            if (res.status >= 400) throw new Error("Credentials could not be removed")
            await fetchUserData()
            setShowDeleteModal(false)
            setCredentialsId("")
        } catch {
            setRemoveError(true)
        } finally {
            setIsRemoving(false)
        }
    }

    const cancelDelete = () => {
        if (isRemoving) return
        setCredentialsId("")
        setRemoveError(false)
        setShowDeleteModal(false)
    }

    const toggleDisabled = async (id: string, disabled: boolean) => {
        setUpdatingCredentialsId(id)
        setUpdateError(false)
        try {
            const res = await doFetch("/network-credentials/disabled", {
                network: network.name,
                credentialsId: id,
                disabled,
            }, "PATCH")
            if (res.status >= 400) throw new Error("Credentials could not be updated")
            await fetchUserData()
        } catch {
            setUpdateError(true)
        } finally {
            setUpdatingCredentialsId("")
        }
    }

    const renderValue = (valueObj: INetworkIntegration["values"][number], field: INetworkIntegration["fields"][number]) => {
        if (field.secret) return <span className="network-settings-secret-value" aria-label="Hidden credential">••••••••</span>
        const value = valueObj[field.name]
        if (!Array.isArray(value)) return typeof value === "string" && value ? value : <span className="text-muted">—</span>
        return value.map((entry, index) => (
            <div key={index} className="network-settings-multi-value">
                {Object.values(entry).join(" · ")}
            </div>
        ))
    }

    return (
        <>
            <div className="network-settings-section-title">Saved credentials</div>
            <div className="network-settings-section-copy">Validated credentials and their current synchronization status.</div>
            {updateError && <div className="text-danger small mb-3">Credentials could not be updated. Please try again.</div>}

            {network.values.length ? (
                <div className="table-responsive network-settings-table-wrap">
                    <Table hover size="sm" className="align-middle mb-0 network-settings-credentials-table">
                        <thead>
                            <tr>
                                {network.fields.map((field) => <th key={field.name}>{field.displayName || field.name}</th>)}
                                <th>Sync status</th>
                                <th aria-label="Actions" />
                            </tr>
                        </thead>
                        <tbody>
                            {network.values.map((valueObj, index) => {
                                const value = valueObj as any
                                const initialized = value._wasInitialized === true
                                const disabled = value._isDisabled === true
                                const hasSetupError = value._hasError === true
                                return (
                                    <tr key={value._id || index}>
                                        {network.fields.map((field) => <td key={field.name}>{renderValue(valueObj, field)}</td>)}
                                        <td>
                                            <div className="network-settings-sync-status">
                                                <Badge bg={disabled ? "secondary" : hasSetupError ? "warning" : initialized ? "success" : "light"} text={!disabled && (hasSetupError || !initialized) ? "dark" : undefined} className="network-settings-sync-badge">
                                                    {disabled ? "Disabled" : hasSetupError ? "Setup issue" : initialized ? "Synchronized" : "Synchronizing"}
                                                </Badge>
                                                {hasSetupError && <div className="network-settings-support-note">Something went wrong during setup. Support can help resolve it.</div>}
                                            </div>
                                        </td>
                                        <td className="text-end">
                                            {value._id && (initialized || hasSetupError) ? (
                                                <div className="d-flex justify-content-end align-items-center gap-2">
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant={disabled ? "outline-success" : "outline-secondary"}
                                                        disabled={Boolean(updatingCredentialsId) || isRemoving}
                                                        onClick={() => toggleDisabled(value._id, !disabled)}
                                                    >
                                                        {updatingCredentialsId === value._id && <Spinner animation="border" size="sm" className="me-2" aria-hidden="true" />}
                                                        {disabled ? "Enable" : "Disable"}
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="link"
                                                        className="network-settings-delete-button"
                                                        aria-label={`Delete ${network.name} credentials ${index + 1}`}
                                                        disabled={Boolean(updatingCredentialsId) || isRemoving}
                                                        onClick={() => handleDelete(value._id)}
                                                    >
                                                        <MdDelete aria-hidden="true" />
                                                    </Button>
                                                </div>
                                            ) : <span className="text-muted">—</span>}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </Table>
                </div>
            ) : (
                <div className="network-settings-empty-state">
                    <div className="fw-semibold mb-1">No credentials configured</div>
                    <div className="text-muted small">Use the form to add the first credential set.</div>
                </div>
            )}

            <Modal show={showDeleteModal} onHide={cancelDelete} centered>
                <Modal.Header closeButton={!isRemoving}><Modal.Title>Delete credentials?</Modal.Title></Modal.Header>
                <Modal.Body>
                    <p>These credentials and their associated dashboard data will be removed.</p>
                    <p className="text-muted small mb-0">This action cannot be undone.</p>
                    {removeError && <div className="text-danger small mt-3">Credentials could not be removed. Please try again.</div>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={cancelDelete} disabled={isRemoving}>Cancel</Button>
                    <Button variant="danger" onClick={confirmDelete} disabled={isRemoving}>
                        {isRemoving ? <><Spinner animation="border" size="sm" aria-hidden="true" /><span className="ms-2">Deleting…</span></> : "Delete credentials"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}
